import { supabase } from "../lib/supabase";

export class ProjectService {
  // Status validation hook - set by the main app
  static _statusValidationHook = null;

  static setStatusValidationHook(hook) {
    ProjectService._statusValidationHook = hook;
  }

  static transformProjectData(project) {
    return {
      id: project.id,
      projectName: project.name,
      jobNumber: project.job_number,
      jobRevNumber: project.rev,
      rev: project.rev,
      modelingStatus: project.modeling_status,
      modelingState: project.modeling_state,
      billingStatus: this.transformBillingStatusFromDb(project.billing_status),
      agreementStatus: project.agreement_status,
      dueDate: project.due_date,
      completionDate: project.completed_date,
      completedDate: project.completed_date,
      finalProjectedCompletionDate: project.final_projected_completion_date,
      projectedCompletionDate: project.projected_completion_date,
      erosionProjectedCompletionDate: project.erosion_projected_completion_date,
      erosionDueDate: project.erosion_due_date,
      erosionProjectId: project.erosion_project_id,
      finishGradeDueDate: project.finish_grade_due_date,
      discrepancies: null,
      modelBuilderId: project.model_builder_id,
      modelBuilderEmployee: project.model_builder_employee
        ? {
            id: project.model_builder_employee.id,
            name: project.model_builder_employee.name,
            email: project.model_builder_employee.email,
          }
        : null,
      isHighPriority: project.is_high_priority,
      isSoftHold: project.is_soft_hold,
      softHoldDate: project.soft_hold_date,
      clientName: project.clients?.name || "No Client",
      clientId: project.client_id,
      clientState: project.clients?.state || null,
      contactName: project.contact?.name || null,
      contactId: project.contact_id,
      primaryContactId: project.contact_id,
      contact: project.contact
        ? {
            id: project.contact.id,
            name: project.contact.name,
            email: project.contact.email,
            phoneNumber: project.contact.phone_number,
            role: project.contact.role,
            clientId: project.contact.client_id,
          }
        : null,
      readyForEmployeeId: project.ready_for_employee_id,
      readyForEmployeeName: project.ready_for_employee?.name || null,
      readyForEmployee: project.ready_for_employee
        ? {
            id: project.ready_for_employee.id,
            name: project.ready_for_employee.name,
            email: project.ready_for_employee.email,
          }
        : null,
      lastModifiedBy: project.last_modified_by_email || null,
      lastModifiedAt: project.last_modified_at,
      lastStageChangeDate: project.last_stage_change_date,
      stageStatus: project.stage_status,
      modelPrice: project.model_price || 0,
      // Use database-calculated value from trigger (uses pricing_tiers table)
      calculatedModelPrice: project.calculated_model_price || null,
      invoicedDate: project.invoiced_date,
      paidDate: project.paid_date,
      // Project Information fields
      modelRequestAdditionalInfo: project.model_request_additional_info || null,
      additionalRequests: project.additional_requests || [],
      fileFormatsNeeded: project.file_formats_needed || [],
      // Project Setup fields
      initialAcceptance: project.initial_acceptance,
      modelRequestClient: project.model_request_client,
      modelRequestContact: project.model_request_contact,
      sendToBilling: project.send_to_billing,
      approximateAcreage: project.approximate_acreage,
      templateFilter: (() => {
        const filter = project.template_filter;
        if (!filter) return [];
        if (Array.isArray(filter)) return filter;
        if (typeof filter === "string") {
          try {
            const parsed = JSON.parse(filter);
            return Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            return [];
          }
        }
        return [];
      })(),
      createSubitems: project.create_subitems || false,
      slackChannelId: project.slack_channel_id,
      // Invoice-related fields
      readyToInvoice: project.ready_to_invoice,
      poNumber: project.po_number,
      invoiceDate: project.invoiced_date, // Using invoiced_date which exists
      price: project.model_price, // Using model_price which exists
      clientOf: project.clients?.client_of, // Get client_of from the clients relationship
      // Hold-related fields
      holdStartDate: project.hold_start_date,
      holdReason: project.hold_reason,
      holdNotes: project.hold_notes,
      holdInitialFollowUpDays: project.hold_initial_follow_up_days,
      holdRecurringFollowUpDays: project.hold_recurring_follow_up_days,
      holdReminderSentDates: project.hold_reminder_sent_dates,
      file_attachments: project.file_attachments || [],
      // Address field mapping for AllJobsTable
      address: project.location || null,
      // Files received fields
      pdfsReceived: project.pdfs_received || false,
      cadFilesReceived: project.cad_files_received || false,
      pdfsReceivedDate: project.pdfs_received_date,
      cadFilesReceivedDate: project.cad_files_received_date,
      projectSetupComplete: project.project_setup_complete || false,
      setupTriggered: project.setup_triggered || false,
      usState: project.us_state || null,
      // Project type
      projectTypeId: project.project_type_id || null,
      projectTypeName: project.project_type?.name || null,
      // Revision fields
      isRevision: project.is_revision || false,
      parentProjectId: project.parent_project_id || null,
      // NC Discount fields
      discountPercentage: project.discount_percentage || 0,
      tnRevenue: project.tn_revenue,
      // Note: Subitems are now loaded on-demand when viewing project details
      // to improve initial page load performance
    };
  }
  static async getProjectWithCalculatedPrice(projectId) {
    try {
      const { data, error } = await supabase
        .from("projects_with_job_display")
        .select("id, calculated_model_price")
        .eq("id", projectId)
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error("Error fetching calculated price:", err);
      return null;
    }
  }

  static async getAllProjects(options = {}) {
    try {
      // Test contacts access on first load
      if (!this._contactsTestDone) {
        this._contactsTestDone = true;
        this.testContactsAccess();
      }
      const { data, error } = await supabase
        .from("projects")
        .select(
          `
          id,
          name,
          job_number,
          rev,
          modeling_status,
          modeling_state,
          billing_status,
          agreement_status,
          due_date,
          completed_date,
          projected_completion_date,
          erosion_projected_completion_date,
          erosion_due_date,
          final_projected_completion_date,
          finish_grade_due_date,
          model_builder_id,
          is_high_priority,
          is_soft_hold,
          soft_hold_date,
          client_id,
          contact_id,
          ready_for_employee_id,
          last_modified_by_email,
          last_modified_at,
          last_stage_change_date,
          stage_status,
          model_request_additional_info,
          additional_requests,
          file_formats_needed,
          initial_acceptance,
          model_request_client,
          model_request_contact,
          send_to_billing,
          approximate_acreage,
          template_filter,
          file_attachments,
          create_subitems,
          slack_channel_id,
          model_price,
          invoiced_date,
          paid_date,
          ready_to_invoice,
          po_number,
          hold_start_date,
          hold_reason,
          hold_notes,
          hold_send_email,
          hold_discrepancy_ids,
          hold_initial_follow_up_days,
          hold_recurring_follow_up_days,
          hold_reminder_sent_dates,
          location,
          pdfs_received,
          cad_files_received,
          pdfs_received_date,
          cad_files_received_date,
          project_setup_complete,
          us_state,
          is_revision,
          parent_project_id,
          project_type_id,
          project_type:template_categories!project_type_id (
            id,
            name
          ),
          clients (
            id,
            name,
            client_of,
            state,
            trimble_formats
          ),
          contact:contacts (
            id,
            name,
            email,
            phone_number,
            role,
            client_id
          ),
          ready_for_employee:employees!ready_for_employee_id (
            id,
            name,
            email
          ),
          model_builder_employee:employees!model_builder_id (
            id,
            name,
            email
          )
        `,
        )
        .order("last_modified_at", { ascending: false });
      // Removed limit to ensure all projects are fetched, especially for overdue invoices

      if (error) {
        console.error('❌ [ProjectService] Supabase error:', error);
        console.error('  - Error code:', error.code);
        console.error('  - Error details:', error.details);
        console.error('  - Error hint:', error.hint);
        throw new Error(`Failed to fetch projects: ${error.message}`);
      }
      // Fetch calculated prices from the view
      const projectIds = data?.map(p => p.id) || [];
      let calculatedPrices = {};
      
      if (projectIds.length > 0) {
        // Batch the requests to avoid URL length limits
        const BATCH_SIZE = 100;
        const batches = [];
        
        for (let i = 0; i < projectIds.length; i += BATCH_SIZE) {
          batches.push(projectIds.slice(i, i + BATCH_SIZE));
        }
        
        try {
          // Fetch all batches in parallel
          const batchPromises = batches.map(batch => 
            supabase
              .from("projects_with_job_display")
              .select("id, calculated_model_price")
              .in("id", batch)
          );
          
          const batchResults = await Promise.all(batchPromises);
          
          // Combine all results
          for (const { data: priceData, error: priceError } of batchResults) {
            if (!priceError && priceData) {
              // Add to the price map
              priceData.forEach(item => {
                calculatedPrices[item.id] = item.calculated_model_price;
              });
            }
          }
        } catch (priceError) {
          // Log error but don't fail the entire request
          console.warn("Error fetching calculated prices:", priceError);
          // Continue without prices rather than failing completely
        }
      }

      const processedData = data.map((project, index) => {

        // Handle legacy data format for backward compatibility
        if (!project.clients && project["clients!client_id"]) {
          project.clients = project["clients!client_id"];
        }
        if (!project.contact && project.contacts) {
          project.contact = project.contacts;
        }

        // Add calculated price from the view
        if (calculatedPrices[project.id] !== undefined) {
          project.calculated_model_price = calculatedPrices[project.id];
        }

        return this.transformProjectData(project);
      });

      return processedData;
    } catch (error) {
      console.error("Error fetching projects:", error);
      throw error;
    }
  }

  static async getProjectsPaginated(options = {}) {
    try {
      const {
        page = 1,
        limit = 100,
        modelingStatus,
        modelingState,
        billingStatus,
        clientId,
        searchTerm,
      } = options;

      let query = supabase.from("projects").select(
        `
          id,
          name,
          job_number,
          rev,
          modeling_status,
          modeling_state,
          billing_status,
          agreement_status,
          due_date,
          completed_date,
          projected_completion_date,
          erosion_projected_completion_date,
          erosion_due_date,
          final_projected_completion_date,
          finish_grade_due_date,
          model_builder_id,
          is_high_priority,
          is_soft_hold,
          soft_hold_date,
          client_id,
          contact_id,
          ready_for_employee_id,
          last_modified_by_email,
          last_modified_at,
          last_stage_change_date,
          stage_status,
          model_request_additional_info,
          additional_requests,
          file_formats_needed,
          initial_acceptance,
          model_request_client,
          model_request_contact,
          send_to_billing,
          approximate_acreage,
          template_filter,
          create_subitems,
          slack_channel_id,
          model_price,
          invoiced_date,
          paid_date,
          ready_to_invoice,
          po_number,
          hold_start_date,
          hold_reason,
          hold_notes,
          hold_send_email,
          hold_discrepancy_ids,
          hold_initial_follow_up_days,
          hold_recurring_follow_up_days,
          hold_reminder_sent_dates,
          location,
          pdfs_received,
          cad_files_received,
          pdfs_received_date,
          cad_files_received_date,
          project_setup_complete,
          us_state,
          is_revision,
          parent_project_id,
          clients (
            id,
            name,
            client_of,
            state,
            trimble_formats
          ),
          contact:contacts (
            id,
            name,
            email,
            phone_number,
            role,
            client_id
          ),
          ready_for_employee:employees!ready_for_employee_id (
            id,
            name,
            email
          ),
          model_builder_employee:employees!model_builder_id (
            id,
            name,
            email
          )
        `,
        { count: "exact" },
      );

      // Apply filters
      if (modelingStatus) {
        query = query.eq("modeling_status", modelingStatus);
      }

      if (modelingState) {
        query = query.eq("modeling_state", modelingState);
      }

      if (billingStatus) {
        query = query.eq("billing_status", billingStatus);
      }

      if (clientId) {
        query = query.eq("client_id", clientId);
      }

      // Apply search if provided (basic search on name and job_number)
      if (searchTerm) {
        query = query.or(
          `name.ilike.%${searchTerm}%,job_number.ilike.%${searchTerm}%`,
        );
      }

      // Apply pagination
      const startRange = (page - 1) * limit;
      const endRange = startRange + limit - 1;

      query = query
        .range(startRange, endRange)
        .order("created_at", { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to fetch projects: ${error.message}`);
      }

      // Fetch calculated prices from the view
      const projectIds = data?.map(p => p.id) || [];
      let calculatedPrices = {};

      if (projectIds.length > 0) {
        const { data: priceData } = await supabase
          .from("projects_with_job_display")
          .select("id, calculated_model_price")
          .in("id", projectIds);

        if (priceData) {
          calculatedPrices = priceData.reduce((acc, item) => {
            acc[item.id] = item.calculated_model_price;
            return acc;
          }, {});
        }
      }

      const processedData = (data || []).map((project) => {
        // Handle legacy data format for backward compatibility
        if (!project.clients && project["clients!client_id"]) {
          project.clients = project["clients!client_id"];
        }
        if (!project.contact && project.contacts) {
          project.contact = project.contacts;
        }

        // Add calculated price from the view
        if (calculatedPrices[project.id] !== undefined) {
          project.calculated_model_price = calculatedPrices[project.id];
        }

        return this.transformProjectData(project);
      });

      return {
        projects: processedData,
        totalCount: count,
        currentPage: page,
        totalPages: Math.ceil(count / limit),
        hasNextPage: endRange < count - 1,
        hasPreviousPage: page > 1,
      };
    } catch (error) {
      console.error("Error fetching paginated projects:", error);
      throw error;
    }
  }

  /**
   * Get paginated completed projects with server-side search
   * Sorted by last_stage_change_date (most recent status change first)
   */
  static async getCompletedProjectsPaginated(options = {}) {
    try {
      const {
        page = 1,
        limit = 50,
        searchTerm = "",
      } = options;

      // Build base query
      let query = supabase.from("projects").select(
        `
          id,
          name,
          job_number,
          rev,
          modeling_status,
          modeling_state,
          billing_status,
          agreement_status,
          due_date,
          completed_date,
          projected_completion_date,
          erosion_projected_completion_date,
          erosion_due_date,
          final_projected_completion_date,
          finish_grade_due_date,
          model_builder_id,
          is_high_priority,
          is_soft_hold,
          soft_hold_date,
          client_id,
          contact_id,
          ready_for_employee_id,
          last_modified_by_email,
          last_modified_at,
          last_stage_change_date,
          stage_status,
          model_request_additional_info,
          additional_requests,
          file_formats_needed,
          initial_acceptance,
          model_request_client,
          model_request_contact,
          send_to_billing,
          approximate_acreage,
          template_filter,
          create_subitems,
          slack_channel_id,
          model_price,
          invoiced_date,
          paid_date,
          ready_to_invoice,
          po_number,
          hold_start_date,
          hold_reason,
          hold_notes,
          hold_send_email,
          hold_discrepancy_ids,
          hold_initial_follow_up_days,
          hold_recurring_follow_up_days,
          hold_reminder_sent_dates,
          location,
          pdfs_received,
          cad_files_received,
          pdfs_received_date,
          cad_files_received_date,
          project_setup_complete,
          us_state,
          is_revision,
          parent_project_id,
          clients (
            id,
            name,
            client_of,
            state,
            trimble_formats
          ),
          contact:contacts (
            id,
            name,
            email,
            phone_number,
            role,
            client_id
          ),
          ready_for_employee:employees!ready_for_employee_id (
            id,
            name,
            email
          ),
          model_builder_employee:employees!model_builder_id (
            id,
            name,
            email
          )
        `,
        { count: "exact" },
      );

      // Filter for completed projects
      query = query.eq("modeling_status", "Completed");

      // Apply search if provided (search across name, job_number, client name, contact name, model builder)
      if (searchTerm) {
        // For searching related tables, we need to use or() with proper syntax
        // Note: Supabase doesn't easily support searching across joined tables in a single query
        // We'll search on the main table fields first
        query = query.or(
          `name.ilike.%${searchTerm}%,job_number.ilike.%${searchTerm}%`,
        );
      }

      // Apply pagination
      const startRange = (page - 1) * limit;
      const endRange = startRange + limit - 1;

      // Sort by last_stage_change_date (most recent status change first)
      query = query
        .range(startRange, endRange)
        .order("last_stage_change_date", { ascending: false, nullsFirst: false });

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to fetch completed projects: ${error.message}`);
      }

      // Fetch calculated prices from the view
      const projectIds = data?.map(p => p.id) || [];
      let calculatedPrices = {};

      if (projectIds.length > 0) {
        const { data: priceData } = await supabase
          .from("projects_with_job_display")
          .select("id, calculated_model_price")
          .in("id", projectIds);

        if (priceData) {
          calculatedPrices = priceData.reduce((acc, item) => {
            acc[item.id] = item.calculated_model_price;
            return acc;
          }, {});
        }
      }

      const processedData = (data || []).map((project) => {
        // Handle legacy data format for backward compatibility
        if (!project.clients && project["clients!client_id"]) {
          project.clients = project["clients!client_id"];
        }
        if (!project.contact && project.contacts) {
          project.contact = project.contacts;
        }

        // Add calculated price from the view
        if (calculatedPrices[project.id] !== undefined) {
          project.calculated_model_price = calculatedPrices[project.id];
        }

        return this.transformProjectData(project);
      });

      return {
        projects: processedData,
        totalCount: count,
        currentPage: page,
        totalPages: Math.ceil(count / limit),
        hasNextPage: endRange < count - 1,
        hasPreviousPage: page > 1,
      };
    } catch (error) {
      console.error("Error fetching completed projects:", error);
      throw error;
    }
  }

  /**
   * Search ALL projects (not just completed) for the completed tab search
   * This allows searching across all statuses when using the search bar
   */
  static async searchAllProjectsForCompleted(searchTerm, limit = 100) {
    try {
      if (!searchTerm || searchTerm.trim() === "") {
        return { projects: [], totalCount: 0 };
      }

      let query = supabase.from("projects").select(
        `
          id,
          name,
          job_number,
          rev,
          modeling_status,
          modeling_state,
          billing_status,
          agreement_status,
          due_date,
          completed_date,
          projected_completion_date,
          erosion_projected_completion_date,
          erosion_due_date,
          final_projected_completion_date,
          finish_grade_due_date,
          model_builder_id,
          is_high_priority,
          is_soft_hold,
          soft_hold_date,
          client_id,
          contact_id,
          ready_for_employee_id,
          last_modified_by_email,
          last_modified_at,
          last_stage_change_date,
          stage_status,
          model_request_additional_info,
          additional_requests,
          file_formats_needed,
          initial_acceptance,
          model_request_client,
          model_request_contact,
          send_to_billing,
          approximate_acreage,
          template_filter,
          create_subitems,
          slack_channel_id,
          model_price,
          invoiced_date,
          paid_date,
          ready_to_invoice,
          po_number,
          hold_start_date,
          hold_reason,
          hold_notes,
          hold_send_email,
          hold_discrepancy_ids,
          hold_initial_follow_up_days,
          hold_recurring_follow_up_days,
          hold_reminder_sent_dates,
          location,
          pdfs_received,
          cad_files_received,
          pdfs_received_date,
          cad_files_received_date,
          project_setup_complete,
          us_state,
          is_revision,
          parent_project_id,
          clients (
            id,
            name,
            client_of,
            state,
            trimble_formats
          ),
          contact:contacts (
            id,
            name,
            email,
            phone_number,
            role,
            client_id
          ),
          ready_for_employee:employees!ready_for_employee_id (
            id,
            name,
            email
          ),
          model_builder_employee:employees!model_builder_id (
            id,
            name,
            email
          )
        `,
        { count: "exact" },
      );

      // Search across name and job_number
      query = query.or(
        `name.ilike.%${searchTerm}%,job_number.ilike.%${searchTerm}%`,
      );

      // Limit results and sort by most recent
      query = query
        .limit(limit)
        .order("last_modified_at", { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        throw new Error(`Failed to search projects: ${error.message}`);
      }

      // Fetch calculated prices from the view
      const projectIds = data?.map(p => p.id) || [];
      let calculatedPrices = {};

      if (projectIds.length > 0) {
        const { data: priceData } = await supabase
          .from("projects_with_job_display")
          .select("id, calculated_model_price")
          .in("id", projectIds);

        if (priceData) {
          calculatedPrices = priceData.reduce((acc, item) => {
            acc[item.id] = item.calculated_model_price;
            return acc;
          }, {});
        }
      }

      const processedData = (data || []).map((project) => {
        if (!project.clients && project["clients!client_id"]) {
          project.clients = project["clients!client_id"];
        }
        if (!project.contact && project.contacts) {
          project.contact = project.contacts;
        }

        if (calculatedPrices[project.id] !== undefined) {
          project.calculated_model_price = calculatedPrices[project.id];
        }

        return this.transformProjectData(project);
      });

      return {
        projects: processedData,
        totalCount: count,
      };
    } catch (error) {
      console.error("Error searching projects:", error);
      throw error;
    }
  }

  /**
   * Get paginated invoice projects (follows ContactsTable pattern)
   * Filters for projects that need invoicing attention
   */
  static async getPaginatedInvoiceProjects(page = 1, pageSize = 50, sortField = "jobNumber", sortDirection = "asc", searchQuery = "", billingStatusFilter = "all") {
    try {
      const offset = (page - 1) * pageSize;

      // Build base query for invoice-relevant projects
      let baseFilter = [];

      // Invoice filtering logic - projects that need invoicing attention
      if (billingStatusFilter === "all") {
        // All invoice-relevant projects
        baseFilter.push("ready_to_invoice.eq.Yes");
        baseFilter.push("billing_status.in.(Quoted,Invoiced)");
        baseFilter.push("and(modeling_status.eq.Completed,or(billing_status.is.null,billing_status.eq.N/A,billing_status.eq.))");
      } else if (billingStatusFilter === "ready") {
        baseFilter.push("and(ready_to_invoice.eq.Yes,not.billing_status.in.(Invoiced,Paid))");
      } else if (billingStatusFilter === "pending") {
        baseFilter.push("billing_status.eq.Quoted");
      } else if (billingStatusFilter === "invoiced") {
        baseFilter.push("billing_status.eq.Invoiced");
      } else if (billingStatusFilter === "paid") {
        baseFilter.push("billing_status.eq.Paid");
      }

      // Build count query
      let countQuery = supabase
        .from("projects")
        .select("*", { count: "exact", head: true });

      // Apply invoice filter to count
      if (baseFilter.length > 0) {
        countQuery = countQuery.or(baseFilter.join(","));
      }

      // Build data query
      let dataQuery = supabase
        .from("projects")
        .select(`
          id,
          name,
          job_number,
          rev,
          modeling_status,
          billing_status,
          model_price,
          discount_percentage,
          tn_revenue,
          invoiced_date,
          paid_date,
          po_number,
          ready_to_invoice,
          client_id,
          clients (
            id,
            name
          )
        `);

      // Apply invoice filter to data query
      if (baseFilter.length > 0) {
        dataQuery = dataQuery.or(baseFilter.join(","));
      }

      // Apply search filters if search query is provided
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.trim();

        // Apply search to count query
        countQuery = countQuery.or(
          `name.ilike.%${query}%,job_number.ilike.%${query}%,po_number.ilike.%${query}%`
        );

        // Apply search to data query
        dataQuery = dataQuery.or(
          `name.ilike.%${query}%,job_number.ilike.%${query}%,po_number.ilike.%${query}%`
        );
      }

      // Get total count for pagination info
      const { count, error: countError } = await countQuery;

      if (countError) {
        console.error('Count query error:', countError);
        throw new Error(`Failed to get invoice projects count: ${countError.message || countError.toString() || 'Unknown error'}`);
      }

      // Handle sorting - map UI field names to database field names
      let actualSortField = sortField;
      const fieldMapping = {
        "projectName": "name",
        "jobNumber": "job_number",
        "clientName": "clients(name)",
        "modelPrice": "model_price",
        "billingStatus": "billing_status",
        "invoiceDate": "invoiced_date",
        "paidDate": "paid_date",
        "poNumber": "po_number"
      };

      // Apply field mapping if it exists
      if (fieldMapping[sortField]) {
        actualSortField = fieldMapping[sortField];
      }

      // Get paginated data with sorting and range
      const { data, error } = await dataQuery
        .order(actualSortField, { ascending: sortDirection === "asc" })
        .range(offset, offset + pageSize - 1);

      if (error) {
        console.error('Data query error:', error);
        throw new Error(`Failed to fetch invoice projects: ${error.message || error.toString() || 'Unknown error'}`);
      }

      const processedData = data.map((project) => ({
        id: project.id,
        projectName: project.name,
        jobNumber: project.job_number,
        rev: project.rev,
        modelingStatus: project.modeling_status,
        billingStatus: this.transformBillingStatusFromDb(project.billing_status),
        modelPrice: project.model_price,
        discountPercentage: project.discount_percentage || 0,
        tnRevenue: project.tn_revenue,
        invoicedDate: project.invoiced_date,
        invoiceDate: project.invoiced_date, // Alias for consistency
        paidDate: project.paid_date,
        poNumber: project.po_number,
        readyToInvoice: project.ready_to_invoice,
        clientId: project.client_id,
        clientName: project.clients?.name || "No Client",
      }));

      return {
        data: processedData,
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        currentPage: page,
      };
    } catch (error) {
      console.error("Error fetching paginated invoice projects:", error);
      throw error;
    }
  }

  /**
   * Get the next job number from the database (single source of truth).
   *
   * Calls the get_next_job_number() RPC, which computes MAX(job_number)+1 under an
   * advisory lock with the same <9000 ceiling as the assign-on-setup trigger. This
   * replaces the old client-side Math.max() over stale board state, which could hand
   * two projects the same number. The uniq_primary_job_number partial index is the
   * hard backstop that rejects a duplicate if two creates still race.
   *
   * @returns {Promise<string>} next job number as a string, e.g. "2615"
   */
  static async getNextJobNumber() {
    const { data, error } = await supabase.rpc("get_next_job_number");
    if (error) throw error;
    return data != null ? String(data) : null;
  }

  static async createProject(projectData) {
    try {
      // Get current user for change tracking
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("projects")
        .insert([
          {
            name: projectData.name || projectData.projectName,
            job_number: projectData.job_number || projectData.jobNumber,
            rev: projectData.rev === undefined ? 0 : projectData.rev,
            modeling_status:
              projectData.modeling_status ||
              projectData.modelingStatus ||
              "Startup",
            modeling_state:
              projectData.modeling_state ||
              projectData.modelingState ||
              "Active",
            billing_status:
              projectData.billing_status ||
              this.transformBillingStatus(projectData.billingStatus),
            agreement_status: projectData.agreementStatus,
            due_date: projectData.dueDate,
            location: projectData.location,
            model_price: projectData.modelPrice,
            approximate_acreage:
              projectData.approximate_acreage || projectData.approximateAcreage,
            template_filter:
              projectData.template_filter || projectData.templateFilter || [],
            additional_requests:
              projectData.additional_requests ||
              projectData.additionalRequests ||
              [],
            is_high_priority: projectData.isHighPriority || false,
            client_id: projectData.client_id || projectData.clientId,
            contact_id: projectData.contact_id || projectData.contactId,
            us_state: projectData.us_state || projectData.usState || null,
            is_revision: projectData.is_revision || false,
            parent_project_id: projectData.parent_project_id || null,
            project_type_id: projectData.project_type_id || null,
            is_linework_only: projectData.is_linework_only || false,
            last_modified_by_email: user?.email,
            last_modified_at: new Date().toISOString(),
          },
        ])
        .select(
          `
          id,
          name,
          job_number,
          modeling_status,
          billing_status,
          agreement_status,
          due_date,
          location,
          model_price,
          approximate_acreage,
          completed_date,
          projected_completion_date,
          is_high_priority,
          client_id,
          contact_id,
          last_modified_by_email,
          last_modified_at,
          clients (
            id,
            name
          ),
          contact:contacts (
            id,
            name,
            email,
            phone_number,
            role,
            client_id
          )
        `,
        )
        .single();

      if (error) {
        throw new Error(`Failed to create project: ${error.message}`);
      }

      return {
        id: data.id,
        projectName: data.name,
        jobNumber: data.job_number,
        rev: data.rev,
        modelingStatus: data.modeling_status,
        modelingState: data.modeling_state,
        billingStatus: this.transformBillingStatusFromDb(data.billing_status),
        agreementStatus: data.agreement_status,
        dueDate: data.due_date,
        location: data.location,
        modelPrice: data.model_price,
        approximateAcreage: data.approximate_acreage,
        completedDate: data.completed_date,
        finalProjectedCompletionDate: data.projected_completion_date,
        modelBuilderId: data.model_builder_id,
        modelBuilderEmployee: data.model_builder_employee
          ? {
              id: data.model_builder_employee.id,
              name: data.model_builder_employee.name,
              email: data.model_builder_employee.email,
            }
          : null,
        discrepancies: null,
        isHighPriority: data.is_high_priority,
        isSoftHold: data.is_soft_hold,
        softHoldDate: data.soft_hold_date,
        clientName: data.clients?.name || "No Client",
        clientId: data.client_id,
        contactName: data.contact?.name || null,
        contactId: data.contact_id,
        contact: data.contact
          ? {
              id: data.contact.id,
              name: data.contact.name,
              email: data.contact.email,
              phoneNumber: data.contact.phone_number,
              role: data.contact.role,
              clientId: data.contact.client_id,
            }
          : null,
        // Ready-for employee data
        readyForEmployeeId: data.ready_for_employee_id,
        readyForEmployeeName: data.ready_for_employee?.name || null,
        readyForEmployee: data.ready_for_employee
          ? {
              id: data.ready_for_employee.id,
              name: data.ready_for_employee.name,
              email: data.ready_for_employee.email,
            }
          : null,
        lastModifiedBy: data.last_modified_by_email || null,
        lastModifiedAt: data.last_modified_at,
        // Add new status tracking fields
        lastStageChangeDate: data.last_stage_change_date,
        stageStatus: data.stage_status,
      };
    } catch (error) {
      console.error("Error creating project:", error);
      throw error;
    }
  }

  static async updateProjectBatch(projectId, updates) {
    try {
      // Get current user for change tracking
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;

      // Check if we're updating modeling status to "Ready to Export"
      const isUpdatingToReadyToExport =
        updates.modeling_status === "Ready to Export";

      // Prepare update data with timestamp tracking
      const updateData = {
        ...updates,
        // Comment out last_modified_by until we figure out the correct field type
        // The database expects an integer but we're trying to save an email
        // last_modified_by: user?.email || null,
        last_modified_at: new Date().toISOString(),
      };
      // For simple trigger updates (like send_to_billing), don't fetch the response
      // to avoid payload size errors on projects with large file_attachments
      const isTriggerOnlyUpdate = ('send_to_billing' in updates) || ('create_subitems_new' in updates);

      let data, error;

      if (isTriggerOnlyUpdate) {
        // Don't select anything back - just update and trigger
        const result = await supabase
          .from("projects")
          .update(updateData)
          .eq("id", projectId);

        error = result.error;
        data = { id: projectId }; // Return minimal data
      } else {
        // Full select for updates that need the response data
        const result = await supabase
          .from("projects")
          .update(updateData)
          .eq("id", projectId)
          .select(
            `
            id,
            name,
            job_number,
            rev,
            modeling_status,
            modeling_state,
            billing_status,
            agreement_status,
            due_date,
            location,
            model_price,
            approximate_acreage,
            completed_date,
            projected_completion_date,
            model_builder_id,
            is_high_priority,
            client_id,
            contact_id,
            ready_for_employee_id,
            ready_to_invoice,
            last_modified_by_email,
            last_modified_at,
            last_stage_change_date,
            stage_status,
            model_request_additional_info,
            additional_requests,
            file_formats_needed,
            initial_acceptance,
            model_request_client,
            model_request_contact,
            send_to_billing,
            approximate_acreage,
            template_filter,
            create_subitems,
            slack_channel_id,
            clients (
              id,
              name,
              brand
            ),
            contact:contacts (
              id,
              name,
              email,
              phone_number,
              role,
              client_id
            ),
            ready_for_employee:employees!ready_for_employee_id (
              id,
              name,
              email
            ),
            model_builder_employee:employees!model_builder_id (
              id,
              name,
              email
            )
          `,
          )
          .single();

        data = result.data;
        error = result.error;
      }

      if (error) {
        console.error("Database update error:", error);
        throw new Error(`Failed to update project: ${error.message}`);
      }

      // Auto-create export record if status changed to "Ready to Export"
      if (isUpdatingToReadyToExport) {
        try {
          // Import the export service
          const { ExportAndSendService } = await import(
            "./exportAndSendService"
          );

          // Check if an export already exists for this project
          const existingExports =
            await ExportAndSendService.getExportsByProjectId(projectId);

          if (existingExports.length === 0) {
            // Get current employee for created_by
            let createdById = null;
            if (user?.email) {
              const { data: employees } = await supabase
                .from("employees")
                .select("id")
                .eq("email", user.email)
                .single();

              if (employees) {
                createdById = employees.id;
              }
            }

            // Determine file formats: use updates, project's formats, or fall back to client brand
            let fileFormatsNeeded = updates.file_formats_needed || data.file_formats_needed || [];
            if (fileFormatsNeeded.length === 0 && data.clients?.brand) {
              // Fallback to client brand (clean up {Trimble} format if present)
              const clientBrand = data.clients.brand;
              if (Array.isArray(clientBrand) && clientBrand.length > 0) {
                fileFormatsNeeded = clientBrand
                  .map((b) => {
                    if (typeof b === "string") {
                      return b.replace(/[{}]/g, "").trim();
                    }
                    return b;
                  })
                  .filter((b) => b && b !== "Unknown");
              }
            }

            // Create a new export record
            await ExportAndSendService.createExport({
              project_id: projectId,
              status: "draft",
              file_formats_needed: fileFormatsNeeded,
              additional_contact_ids: [],
              email_body: "",
              topcon_files: [],
              leica_files: [],
              vcl_files: [],
              dxf_ttm_files: [],
              svd_svl_files: [],
              background_image_files: [],
              created_by: createdById,
            });
          }
        } catch (error) {
          // Don't fail the update if export creation fails
          console.error("Error auto-creating export record:", error);
        }
      }

      return data;
    } catch (error) {
      console.error("Error in updateProjectBatch:", error);
      throw error;
    }
  }

  /**
   * Send hold email for a project (mirrors discrepancy email pattern)
   * @param {number} projectId - The project ID
   * @param {string} emailPreview - The email body content
   * @param {Array} emailTo - Array of contact IDs for To field
   * @param {Array} emailCc - Array of contact IDs for CC field
   * @param {string} emailSubject - The email subject line
   * @param {Array} attachmentUrls - Array of attachment URLs from discrepancies
   * @param {Object} discrepancyData - Discrepancy data to include
   * @param {string} providedHtml - Pre-generated HTML (from OnHoldReminderModal)
   * @returns {Promise<Object>} Updated project
   */
  static async sendHoldEmail(
    projectId,
    emailPreview,
    emailTo = [],
    emailCc = [],
    emailSubject = "",
    attachmentUrls = [],
    discrepancyData = null,
    providedHtml = null
  ) {
    try {
      // Validate payload sizes to prevent "payload string too long" errors
      const maxTextSize = 64000; // ~64KB limit for text fields
      const maxSubjectSize = 255; // Standard email subject limit
      
      if (emailPreview && emailPreview.length > maxTextSize) {
        throw new Error(`Email content is too large (${emailPreview.length} characters). Maximum allowed is ${maxTextSize} characters.`);
      }
      
      if (emailSubject && emailSubject.length > maxSubjectSize) {
        throw new Error(`Email subject is too long (${emailSubject.length} characters). Maximum allowed is ${maxSubjectSize} characters.`);
      }

      // Get the current logged-in user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Convert contact IDs to email addresses
      let emailToAddresses = [];
      let emailCcAddresses = [];

      if (emailTo && emailTo.length > 0) {
        const { data: toContacts } = await supabase
          .from('contacts')
          .select('email')
          .in('id', emailTo);
        emailToAddresses = toContacts ? toContacts.map(c => c.email?.toLowerCase()).filter(Boolean) : [];
      }

      if (emailCc && emailCc.length > 0) {
        const { data: ccContacts } = await supabase
          .from('contacts')
          .select('email')
          .in('id', emailCc);
        emailCcAddresses = ccContacts ? ccContacts.map(c => c.email?.toLowerCase()).filter(Boolean) : [];
      }

      // Generate HTML version of the email (or use provided HTML from OnHoldReminderModal)
      let emailHtml;

      if (providedHtml) {
        // Use pre-generated HTML (from OnHoldReminderModal with buildStructuredEmail)
        emailHtml = providedHtml;
      } else {
        // Generate HTML from plain text preview (for HoldProjectModal compatibility)
        const { formatEmailToHTML } = await import('../utils/emailHtmlFormatter');

        // Get user signature if available
        let userSignature = '';
        if (user?.email) {
          const { data: userData } = await supabase
            .from('employees')
            .select('email_signature_html')
            .eq('email', user.email)
            .single();
          userSignature = userData?.email_signature_html || '';
        }

        // Get contact name for personalization
        let contactName = '';
        if (emailTo && emailTo.length > 0) {
          const { data: primaryContact } = await supabase
            .from('contacts')
            .select('name')
            .eq('id', emailTo[0])
            .single();
          if (primaryContact?.name) {
            contactName = primaryContact.name.split(' ')[0];
          }
        }

        emailHtml = formatEmailToHTML(
          emailPreview,
          userSignature,
          false,
          {},
          {
            name: contactName,
            summary: 'Project hold notification'
          }
        );
      }

      // Save email data to on_hold_email_tracking table
      const trackingData = {
        project_id: projectId,
        email_to: emailToAddresses,
        email_cc: emailCcAddresses,
        email_subject: emailSubject,
        email_preview: emailPreview,
        email_html: emailHtml,
        email_status: 'draft',
        include_discrepancies: discrepancyData ? true : false,
        discrepancies_included: discrepancyData,
        created_by: user?.email || 'unknown',
        updated_by: user?.email || 'unknown',
        last_email_sent_date: new Date().toISOString()
      };

      // Check if tracking record already exists
      const { data: existingTracking } = await supabase
        .from('on_hold_email_tracking')
        .select('id')
        .eq('project_id', projectId)
        .single();

      if (existingTracking) {
        // Reset send_email to false first
        await supabase
          .from('on_hold_email_tracking')
          .update({ send_email: false })
          .eq('id', existingTracking.id);

        // Wait for database to process
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Update existing record with send_email true to trigger notification
        const { error: updateError } = await supabase
          .from('on_hold_email_tracking')
          .update({ ...trackingData, send_email: true })
          .eq('id', existingTracking.id);

        if (updateError) {
          console.error('Error updating email tracking:', updateError);
        }
      } else {
        // Create new record with send_email true
        const { error: insertError } = await supabase
          .from('on_hold_email_tracking')
          .insert([{ ...trackingData, send_email: true }]);

        if (insertError) {
          console.error('Error creating email tracking:', insertError);
        }
      }
      // First set hold_send_email to false to reset the trigger
      const { error: resetError } = await supabase
        .from("projects")
        .update({
          hold_send_email: false,
        })
        .eq("id", projectId);

      if (resetError) {
        console.error("Error resetting hold_send_email:", resetError);
      }

      // Add a small delay to ensure the database processes the false value
      await new Promise((resolve) => setTimeout(resolve, 100));
      console.log("Email data:", {
        emailTo: emailToAddresses,
        emailCc: emailCcAddresses,
        emailSubject,
        emailPreview: emailPreview.substring(0, 100) + "...",
      });

      // Then set it to true along with other updates to trigger the email workflow
      const { data, error } = await supabase
        .from("projects")
        .update({
          hold_email_preview: emailPreview,
          hold_email_to: emailTo,
          hold_email_cc: emailCc,
          hold_email_subject: emailSubject,
          // Removed hold_email_attachment_urls - column doesn't exist in database
          // Attachment URLs are already stored with the discrepancies themselves
          hold_email_sent_date: new Date().toISOString(),
          hold_send_email: true,
          last_modified_by: user?.email || null,
          last_modified_by_email: user?.email || null,
        })
        .eq("id", projectId)
        .select()
        .single();
      if (error) {
        throw new Error(`Failed to send hold email: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error("Error sending hold email:", error);
      throw error;
    }
  }

  /**
   * Send hold email from ModelBuildingManagerDashboard (without attachment URLs)
   * This is a simplified version that doesn't use the hold_email_attachment_urls column
   * @param {number} projectId - The project ID
   * @param {string} emailPreview - The email body content
   * @param {Array} emailTo - Array of contact IDs for To field
   * @param {Array} emailCc - Array of contact IDs for CC field
   * @param {string} emailSubject - The email subject line
   * @returns {Promise<Object>} Updated project
   */
  static async sendHoldEmailFromDashboard(
    projectId,
    emailPreview,
    emailTo = [],
    emailCc = [],
    emailSubject = ""
  ) {
    try {
      // Validate payload sizes to prevent "payload string too long" errors
      const maxTextSize = 64000; // ~64KB limit for text fields
      const maxSubjectSize = 255; // Standard email subject limit
      
      if (emailPreview && emailPreview.length > maxTextSize) {
        throw new Error(`Email content is too large (${emailPreview.length} characters). Maximum allowed is ${maxTextSize} characters.`);
      }
      
      if (emailSubject && emailSubject.length > maxSubjectSize) {
        throw new Error(`Email subject is too long (${emailSubject.length} characters). Maximum allowed is ${maxSubjectSize} characters.`);
      }
      // First set hold_send_email to false to reset the trigger
      const { error: resetError } = await supabase
        .from("projects")
        .update({
          hold_send_email: false,
        })
        .eq("id", projectId);

      if (resetError) {
        console.error("Error resetting hold_send_email:", resetError);
      }

      // Add a small delay to ensure the database processes the false value
      await new Promise((resolve) => setTimeout(resolve, 100));
      // Only set hold_send_email to trigger the n8n workflow
      // All email data is saved in on_hold_email_tracking table
      const { data, error } = await supabase
        .from("projects")
        .update({
          hold_send_email: true,
        })
        .eq("id", projectId)
        .select()
        .single();
      if (error) {
        throw new Error(`Failed to trigger hold email: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error("Error triggering hold email from dashboard:", error);
      throw error;
    }
  }

  static async updateProject(projectId, field, value) {
    try {
      // Skip updates for fields that don't exist in database yet
      if (field === "finalCompletionDate" || field === "discrepancies") {
        console.warn(`Field ${field} not implemented in database yet`);
        return null;
      }

      // Get current user for change tracking
      const { data: authData } =
        await supabase.auth.getUser();
      const user = authData?.user;

      // Debug logging
      if (!user || !user.email) {
        console.warn(
          "WARNING: No authenticated user or email found. Updates will not track email properly.",
        );
      }

      const dbField = this.mapFieldToDatabase(field);
      let transformedValue = value;

      // Transform billing status to match database enum
      if (field === "billingStatus") {
        transformedValue = this.transformBillingStatus(value);
      }

      // ready_to_invoice is an ENUM field that accepts "Yes", "No", "None Needed", or null
      // No transformation needed - pass the string value directly
      if (field === "ready_to_invoice" || field === "readyToInvoice") {
        if (value === "" || value === undefined) {
          transformedValue = null;
        } else {
          transformedValue = value;
        }
      }

      // Check if we're updating modeling status to "Ready to Export"
      const isUpdatingToReadyToExport =
        field === "modelingStatus" && value === "Ready to Export";

      const updateData = {
        [dbField]: transformedValue,
        last_modified_by_email: user?.email || null,
        last_modified_at: new Date().toISOString(),
      };

      console.log("ProjectService.updateProject:", {
        projectId,
        field,
        dbField,
        value: transformedValue,
        updateData,
        specificFields: {
          updatingReadyFor: field === "readyForEmployeeId",
          updatingModelBuilder: field === "modelBuilderId",
          onlyOneField: Object.keys(updateData).length === 3, // field + timestamps
        },
      });

      // Validate modelingStatus changes if validation hook is set
      if (field === "modelingStatus" && ProjectService._statusValidationHook) {
        try {
          const validationResult = await ProjectService._statusValidationHook(
            projectId,
            field,
            transformedValue,
          );

          if (validationResult === false) {
            // Validation rejected the change (user cancelled or validation failed)
            throw new Error("Status change cancelled by user");
          }
        } catch (validationError) {
          console.error(
            "❌ ProjectService: Status validation error:",
            validationError,
          );
          if (validationError.message === "Status change cancelled by user") {
            throw validationError; // Re-throw cancellation
          }
          // For other validation errors, continue with update (fail-safe)
        }
      }

      // For trigger fields like modeling_status, only select the minimal fields needed
      // to avoid fetching massive file_attachments array that causes payload size errors
      const isTriggerField = field === "modelingStatus" || field === "modelingState";

      let data, error;

      if (isTriggerField) {
        // Only select the specific fields that are updated, not ALL project data
        // Include file_formats_needed for auto-export creation when status changes to "Ready to Export"
        console.log("Using minimal select for trigger field update (avoiding file_attachments)");
        const result = await supabase
          .from("projects")
          .update(updateData)
          .eq("id", projectId)
          .select(`
            id,
            name,
            job_number,
            rev,
            modeling_status,
            modeling_state,
            is_high_priority,
            is_soft_hold,
            soft_hold_date,
            last_modified_by_email,
            last_modified_at,
            file_formats_needed,
            client_id,
            clients (
              id,
              name,
              brand
            )
          `)
          .single();

        error = result.error;
        data = result.data;
      } else {
        // Full select for updates that need the response data
        const result = await supabase
          .from("projects")
          .update(updateData)
          .eq("id", projectId)
          .select(
          `
          id,
          name,
          job_number,
          rev,
          modeling_status,
          modeling_state,
          billing_status,
          agreement_status,
          due_date,
          location,
          model_price,
          approximate_acreage,
          completed_date,
          projected_completion_date,
          model_builder_id,
          is_high_priority,
          is_soft_hold,
          soft_hold_date,
          client_id,
          contact_id,
          ready_for_employee_id,
          ready_to_invoice,
          last_modified_by_email,
          last_modified_at,
          last_stage_change_date,
          stage_status,
          model_request_additional_info,
          additional_requests,
          file_formats_needed,
          initial_acceptance,
          model_request_client,
          model_request_contact,
          send_to_billing,
          approximate_acreage,
          template_filter,
          create_subitems,
          slack_channel_id,
          clients (
            id,
            name
          ),
          contact:contacts (
            id,
            name,
            email,
            phone_number,
            role,
            client_id
          ),
          ready_for_employee:employees!ready_for_employee_id (
            id,
            name,
            email
          ),
          model_builder_employee:employees!model_builder_id (
            id,
            name,
            email
          )
        `,
          )
          .single();

        error = result.error;
        data = result.data;
      }

      if (error) {
        console.error("Update failed:", {
          error,
          projectId,
          field,
          dbField,
          originalValue: value,
          transformedValue,
          updateData
        });
        // Provide more specific error for ready_to_invoice field
        if (field === "ready_to_invoice" || field === "readyToInvoice") {
          throw new Error(`Failed to update Ready to Invoice status: ${error.message}. Value provided: "${transformedValue}"`);
        }
        throw new Error(`Failed to update project: ${error.message}`);
      }

      console.log("Update successful, returned data:", {
        id: data.id,
        lastModifiedBy: data.last_modified_by_email,
        lastModifiedAt: data.last_modified_at,
        readyForEmployeeId: data.ready_for_employee_id,
        readyForEmployee: data.ready_for_employee,
        modelBuilderId: data.model_builder_id,
        modelBuilderEmployee: data.model_builder_employee,
        allFields: Object.keys(data),
      });

      // Auto-create export record if status changed to "Ready to Export"
      if (isUpdatingToReadyToExport) {
        try {
          // Import the export service
          const { ExportAndSendService } = await import(
            "./exportAndSendService"
          );

          // Check if an export already exists for this project
          const existingExports =
            await ExportAndSendService.getExportsByProjectId(projectId);

          if (existingExports.length === 0) {
            // Get current employee for created_by
            let createdById = null;
            if (user?.email) {
              const { data: employees } = await supabase
                .from("employees")
                .select("id")
                .eq("email", user.email)
                .single();

              if (employees) {
                createdById = employees.id;
              }
            }

            // Determine file formats: use project's formats, or fall back to client brand
            let fileFormatsNeeded = data.file_formats_needed || [];
            if (fileFormatsNeeded.length === 0 && data.clients?.brand) {
              // Fallback to client brand (clean up {Trimble} format if present)
              const clientBrand = data.clients.brand;
              if (Array.isArray(clientBrand) && clientBrand.length > 0) {
                fileFormatsNeeded = clientBrand
                  .map((b) => {
                    if (typeof b === "string") {
                      return b.replace(/[{}]/g, "").trim();
                    }
                    return b;
                  })
                  .filter((b) => b && b !== "Unknown");
              }
            }

            // Create a new export record
            await ExportAndSendService.createExport({
              project_id: projectId,
              status: "draft",
              file_formats_needed: fileFormatsNeeded,
              additional_contact_ids: [],
              email_body: "",
              topcon_files: [],
              leica_files: [],
              vcl_files: [],
              dxf_ttm_files: [],
              svd_svl_files: [],
              background_image_files: [],
              created_by: createdById,
            });
          } else {
            console.log(
              `Found ${existingExports.length} existing export(s), skipping auto-creation`,
            );
          }
        } catch (error) {
          // Don't fail the update if export creation fails
          console.error("Error auto-creating export record:", error);
        }
      }

      return {
        id: data.id,
        projectName: data.name,
        jobNumber: data.job_number,
        rev: data.rev,
        modelingStatus: data.modeling_status,
        modelingState: data.modeling_state,
        billingStatus: this.transformBillingStatusFromDb(data.billing_status),
        agreementStatus: data.agreement_status,
        dueDate: data.due_date,
        location: data.location,
        modelPrice: data.model_price,
        approximateAcreage: data.approximate_acreage,
        completedDate: data.completed_date,
        finalProjectedCompletionDate: data.projected_completion_date,
        modelBuilderId: data.model_builder_id,
        modelBuilderEmployee: data.model_builder_employee
          ? {
              id: data.model_builder_employee.id,
              name: data.model_builder_employee.name,
              email: data.model_builder_employee.email,
            }
          : null,
        discrepancies: null,
        isHighPriority: data.is_high_priority,
        isSoftHold: data.is_soft_hold,
        softHoldDate: data.soft_hold_date,
        clientName: data.clients?.name || "No Client",
        clientId: data.client_id,
        contactName: data.contact?.name || null,
        contactId: data.contact_id,
        contact: data.contact
          ? {
              id: data.contact.id,
              name: data.contact.name,
              email: data.contact.email,
              phoneNumber: data.contact.phone_number,
              role: data.contact.role,
              clientId: data.contact.client_id,
            }
          : null,
        // Ready-for employee data
        readyForEmployeeId: data.ready_for_employee_id,
        readyForEmployeeName: data.ready_for_employee?.name || null,
        readyForEmployee: data.ready_for_employee
          ? {
              id: data.ready_for_employee.id,
              name: data.ready_for_employee.name,
              email: data.ready_for_employee.email,
            }
          : null,
        readyToInvoice: data.ready_to_invoice,
        lastModifiedBy: data.last_modified_by_email || null,
        lastModifiedAt: data.last_modified_at,
        // Add new status tracking fields
        lastStageChangeDate: data.last_stage_change_date,
        stageStatus: data.stage_status,
        // Project Information fields
        modelRequestAdditionalInfo: data.model_request_additional_info || null,
        additionalRequests: data.additional_requests || [],
        fileFormatsNeeded: data.file_formats_needed || [],
      };
    } catch (error) {
      console.error("Error updating project:", error);
      throw error;
    }
  }

  static async deleteProject(projectId) {
    try {
      // Step 1: Delete related discrepancies
      const { error: discrepanciesError } = await supabase
        .from("discrepancies")
        .delete()
        .eq("project_id", projectId);

      if (discrepanciesError) {
        throw new Error(
          `Failed to delete project discrepancies: ${discrepanciesError.message}`,
        );
      }

      // Step 2: Delete related subitems
      const { error: subitemsError } = await supabase
        .from("subitems")
        .delete()
        .eq("project_id", projectId);

      if (subitemsError) {
        throw new Error(
          `Failed to delete project subitems: ${subitemsError.message}`,
        );
      }

      // Step 3: Delete related export_and_send records
      const { error: exportError } = await supabase
        .from("export_and_send")
        .delete()
        .eq("project_id", projectId);

      if (exportError) {
        throw new Error(
          `Failed to delete project export records: ${exportError.message}`,
        );
      }

      // Step 4: Delete related modeling_status_comments
      const { error: commentsError } = await supabase
        .from("modeling_status_comments")
        .delete()
        .eq("project_id", projectId);

      if (commentsError) {
        throw new Error(
          `Failed to delete project status comments: ${commentsError.message}`,
        );
      }

      // Step 5: Finally delete the project itself
      const { error: projectError } = await supabase
        .from("projects")
        .delete()
        .eq("id", projectId);

      if (projectError) {
        throw new Error(`Failed to delete project: ${projectError.message}`);
      }
      return true;
    } catch (error) {
      console.error("Error deleting project:", error);
      throw error;
    }
  }

  static async getEmployees() {
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("id, name, email, email_signature_html, status_mentions, slack_userid, formatted_slack_userid")
        .order("name", { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch employees: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error("Error fetching employees:", error);
      throw error;
    }
  }

  static async getFilteredEmployees() {
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("id, name, email, email_signature_html, status_mentions, slack_userid, formatted_slack_userid")
        .order("name", { ascending: true});

      if (error) {
        throw new Error(`Failed to fetch employees: ${error.message}`);
      }

      // Filter out employees where status_mentions = ["Admin"] exactly
      const filtered = (data || []).filter((employee) => {
        if (
          !employee.status_mentions ||
          !Array.isArray(employee.status_mentions)
        ) {
          return true; // Include if no status_mentions
        }
        // Exclude if array contains exactly one element "Admin"
        if (
          employee.status_mentions.length === 1 &&
          employee.status_mentions[0] === "Admin"
        ) {
          return false;
        }
        return true;
      });
      return filtered;
    } catch (error) {
      console.error("Error fetching filtered employees:", error);
      throw error;
    }
  }

  static async getClients() {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .order("name");

      if (error) {
        throw new Error(`Failed to fetch clients: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error("Error fetching clients:", error);
      throw error;
    }
  }

  static async getClientsWithBillingInfo() {
    try {
      const { data, error } = await supabase
        .from("clients")
        .select(
          "id, name, billing_status, yearly_cost, clients_invoiced_date, client_of, client_status, state, platform, address, company_phone, brand, trimble_formats, services_provided, lead_source, dealer_name, contractor_name, three_d_utilities, level_of_detail, needs_handicap_ramps, platform_other, pricing_status, terms, acquisition_source, w9_coi_email",
        )
        .order("name");

      if (error) {
        throw new Error(
          `Failed to fetch clients with billing info: ${error.message}`,
        );
      }

      return data || [];
    } catch (error) {
      console.error("Error fetching clients with billing info:", error);
      throw error;
    }
  }

  static async updateClient(clientId, field, value) {
    try {
      const { data, error } = await supabase
        .from("clients")
        .update({ [field]: value })
        .eq("id", clientId)
        .select();

      if (error) {
        console.error("Supabase error:", error);
        throw new Error(`Failed to update client: ${error.message}`);
      }
      return data;
    } catch (error) {
      console.error("Error updating client:", error);
      throw error;
    }
  }

  static async getContacts() {
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select(
          "id, name, email, phone_number, role, client_id, billing_yn, notes",
        )
        .order("name");

      if (error) {
        throw new Error(`Failed to fetch contacts: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error("Error fetching contacts:", error);
      throw error;
    }
  }


  static async getContactsByClient(clientId) {
    try {
      if (!clientId) {
        return [];
      }

      // Ensure clientId is a number
      const numericClientId =
        typeof clientId === "string" ? parseInt(clientId, 10) : clientId;
      // First, let's check if ANY contacts exist in the database
      await supabase
        .from("contacts")
        .select("id, client_id")
        .limit(5);
      // Now try the actual query
      const { data, error } = await supabase
        .from("contacts")
        .select(
          "id, name, email, phone_number, role, client_id, billing_yn, notes",
        )
        .eq("client_id", numericClientId)
        .order("name");
      if (error) {
        console.error("❌ Supabase error:", error);
        throw new Error(
          `Failed to fetch contacts for client: ${error.message}`,
        );
      }

      return data || [];
    } catch (error) {
      console.error("❌ Error in getContactsByClient:", error);
      throw error;
    }
  }

  // Test method to check contacts table access
  static async testContactsAccess() {
    try {
      // Try to fetch a few contacts directly
      const { data: contactsData, error: contactsError } = await supabase
        .from("contacts")
        .select("*")
        .limit(5);

      if (contactsError) {
        console.error("Error accessing contacts table:", contactsError);
        return null;
      }
      // Try to fetch specific contacts by IDs we saw in the logs
      const testIds = [1120, 1208, 1182];
      const { error: specificError } = await supabase
        .from("contacts")
        .select("*")
        .in("id", testIds);

      if (specificError) {
        console.error("Error fetching specific contacts:", specificError);
      }

      return contactsData;
    } catch (error) {
      console.error("Contact access test failed:", error);
      return null;
    }
  }

  static mapFieldToDatabase(field) {
    const fieldMap = {
      projectName: "name",
      jobNumber: "job_number",
      rev: "rev",
      modelingStatus: "modeling_status",
      modelingState: "modeling_state",
      billingStatus: "billing_status",
      dueDate: "due_date",
      completionDate: "completed_date",
      finalProjectedCompletionDate: "projected_completion_date",
      // finalCompletionDate: 'final_completion_date', // Column doesn't exist yet
      // discrepancies: 'discrepancies', // Column doesn't exist yet
      modelBuilderId: "model_builder_id",
      isHighPriority: "is_high_priority",
      isSoftHold: "is_soft_hold",
      softHoldDate: "soft_hold_date",
      clientId: "client_id",
      contactId: "contact_id",
      contact_id: "contact_id",
      readyForEmployeeId: "ready_for_employee_id",
      readyToInvoice: "ready_to_invoice",
      modelPrice: "model_price",
      poNumber: "po_number",
      invoiceDate: "invoiced_date",
      paidDate: "paid_date",
      address: "address",
      agreementStatus: "agreement_status",
      createSlackChannelNew: "create_slack_channel_new",
      slackCreate: "slack_create",
      slackChannelRequestedAt: "slack_channel_requested_at",
    };
    return fieldMap[field] || field;
  }

  static transformBillingStatus(status) {
    // Transform display values to database enum values
    // Try different common PostgreSQL enum formats
    const statusMap = {
      "Ready to Invoice": "Ready_to_Invoice", // Try underscore format
      Quoted: "Quoted",
      Invoiced: "Invoiced",
      Partial: "Partial",
      Paid: "Paid",
    };

    // Log the transformation for debugging
    const transformed = statusMap[status] || status;
    return transformed;
  }

  static transformBillingStatusFromDb(status) {
    // Transform database values back to display format
    const statusMap = {
      Ready_to_Invoice: "Ready to Invoice",
      Quoted: "Quoted",
      Invoiced: "Invoiced",
      Partial: "Partial",
      Paid: "Paid",
    };

    return statusMap[status] || status;
  }

  static async getModelQuotes() {
    try {
      const { data, error } = await supabase
        .from("model_quotes")
        .select(
          `
          id,
          name,
          email,
          company_name,
          project_name,
          state,
          is_revision,
          quote_due_date,
          advanced_utility,
          structure_count,
          erosion_surface_due_date,
          project_info,
          created_at,
          updated_at,
          price,
          quote_status,
          notes_from_ab_civil,
          date_quoted,
          quote_status_change_date,
          client_name,
          additional_requests,
          file_formats,
          client_id,
          contact_id,
          file_attachments
        `,
        )
        .order("created_at", { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch model quotes: ${error.message}`);
      }

      // Filter out quotes with status 'Quoted', 'Accepted', or 'Declined'
      const filteredData = (data || []).filter((quote) => {
        const status = quote.quote_status;
        return (
          !status ||
          (status !== "Quoted" &&
            status !== "Accepted" &&
            status !== "Declined")
        );
      });

      return filteredData;
    } catch (error) {
      console.error("Error in getModelQuotes:", error);
      throw error;
    }
  }

  static async getAllModelQuotes() {
    try {
      // Add timestamp to avoid caching issues after deletes
      Date.now();
      const { data, error } = await supabase
        .from("model_quotes")
        .select(
          `
          id,
          name,
          email,
          company_name,
          project_name,
          state,
          is_revision,
          quote_due_date,
          advanced_utility,
          structure_count,
          erosion_surface_due_date,
          project_info,
          created_at,
          updated_at,
          price,
          quote_status,
          notes_from_ab_civil,
          date_quoted,
          quote_status_change_date,
          client_name,
          additional_requests,
          file_formats,
          client_id,
          contact_id,
          file_attachments
        `,
        )
        .gte('created_at', '1900-01-01T00:00:00Z')
        .order("created_at", { ascending: false });

      if (error) {
        throw new Error(`Failed to fetch model quotes: ${error.message}`);
      }

      // Return all quotes without filtering for the table view
      return data || [];
    } catch (error) {
      console.error("Error fetching model quotes:", error);
      throw error;
    }
  }

  static async updateModelQuote(quoteId, field, value) {
    try {
      await supabase.auth.getUser();

      // First check if the quote exists
      const { data: existingQuote, error: checkError } = await supabase
        .from("model_quotes")
        .select("id")
        .eq("id", quoteId)
        .maybeSingle();

      if (checkError) {
        console.error("Error checking quote existence:", checkError);
        throw new Error(`Failed to verify model quote exists: ${checkError.message}`);
      }

      if (!existingQuote) {
        throw new Error(`Model quote with ID ${quoteId} not found`);
      }

      const updateData = {
        [field]: value,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("model_quotes")
        .update(updateData)
        .eq("id", quoteId)
        .select()
        .maybeSingle();

      if (error) {
        console.error("Update failed:", error);
        throw new Error(`Failed to update model quote: ${error.message}`);
      }

      if (!data) {
        throw new Error(`No rows were updated for quote ID ${quoteId}`);
      }

      return data;
    } catch (error) {
      console.error("Error updating model quote:", error);
      throw error;
    }
  }

  static async updateModelQuoteBatch(quoteId, updates) {
    try {
      // First check if the quote exists
      const { data: existingQuote, error: checkError } = await supabase
        .from("model_quotes")
        .select("id")
        .eq("id", quoteId)
        .maybeSingle();

      if (checkError) {
        console.error("Error checking quote existence:", checkError);
        throw new Error(`Failed to verify model quote exists: ${checkError.message}`);
      }

      if (!existingQuote) {
        throw new Error(`Model quote with ID ${quoteId} not found`);
      }

      const updateData = {
        ...updates,
        updated_at: new Date().toISOString(),
      };
      const { data, error } = await supabase
        .from("model_quotes")
        .update(updateData)
        .eq("id", quoteId)
        .select()
        .maybeSingle();

      if (error) {
        console.error("Database update error:", error);
        throw new Error(`Failed to update model quote: ${error.message}`);
      }

      if (!data) {
        throw new Error(`No rows were updated for quote ID ${quoteId}`);
      }
      return data;
    } catch (error) {
      console.error("Error in updateModelQuoteBatch:", error);
      throw error;
    }
  }

  static async deleteModelQuote(quoteId) {
    try {
      // Get current user for authentication context (required for RLS policies)
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      
      if (!user) {
        throw new Error('User not authenticated - cannot delete model quote');
      }
      // First check if the quote exists
      const { data: existingQuote, error: checkError } = await supabase
        .from("model_quotes")
        .select("id, project_name")
        .eq("id", quoteId)
        .maybeSingle();

      if (checkError) {
        console.error("Error checking quote existence:", checkError);
        throw new Error(`Failed to verify model quote exists: ${checkError.message}`);
      }

      if (!existingQuote) {
        console.error(`No quote found with ID: ${quoteId}`);
        throw new Error(`Model quote with ID ${quoteId} not found`);
      }

      console.log(`Found quote to delete: ${existingQuote.project_name} (ID: ${quoteId})`);

      // Attempt the delete
      const { data: deleteResult, error } = await supabase
        .from("model_quotes")
        .delete()
        .eq("id", quoteId)
        .select();

      if (error) {
        console.error("Delete failed with error:", error);
        throw new Error(`Failed to delete model quote: ${error.message}`);
      }
      if (!deleteResult || deleteResult.length === 0) {
        console.error("Delete failed: No rows were affected (likely RLS policy blocking delete)");
        throw new Error(
          'Delete failed: Unable to delete model quote. ' +
          'This may be due to database permissions (Row Level Security policy). ' +
          'Please contact your administrator to add a DELETE policy for the model_quotes table.'
        );
      }
      return { success: true, deletedQuote: existingQuote };
    } catch (error) {
      console.error("Error in deleteModelQuote:", error);
      throw error;
    }
  }

  /**
   * Get contacts for a specific client
   * @param {number} clientId - The client ID
   * @returns {Promise<Array>} Array of contacts for the client
   */
  static async getContactsByClientId(clientId) {
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select("id, name, email, phone_number, role")
        .eq("client_id", clientId)
        .order("name");

      if (error) {
        throw new Error(`Failed to fetch contacts: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error("Error fetching contacts:", error);
      throw error;
    }
  }

  /**
   * Get base projects for a client (rev = 0, excludes erosion rev = 999)
   * Used for revision dropdowns where user selects the base project
   * @param {number|null} clientId - The client ID to filter by, or null for all
   * @returns {Promise<Array>} Array of base projects with id, job_number, name, contact_id, client_id, project_type_id
   */
  static async getBaseProjectsForClient(clientId = null) {
    try {
      let query = supabase
        .from("projects")
        .select("id, job_number, name, contact_id, client_id, rev, project_type_id")
        // Include projects with rev=0 OR rev=null (new projects not yet set up)
        .or("rev.eq.0,rev.is.null")
        // Exclude projects that are marked as revisions
        .neq("is_revision", true)
        .not("job_number", "is", null);

      // If clientId provided, filter by client
      if (clientId) {
        query = query.eq("client_id", clientId);
      } else {
        // Limit results when fetching all projects
        query = query.limit(200);
      }

      // Sort by ID descending (newest first)
      query = query.order("id", { ascending: false });

      const { data, error } = await query;

      if (error) {
        throw new Error(`Failed to fetch base projects: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error("Error fetching base projects:", error);
      throw error;
    }
  }

  /**
   * Search base projects by name or job number (async search)
   * @param {string} query - Search query (min 2 characters)
   * @param {number|null} clientId - Optional client ID to filter by
   * @returns {Promise<Array>} Matching base projects
   */
  static async searchBaseProjects(query, clientId = null) {
    try {
      if (!query || query.length < 2) {
        return [];
      }

      // Build base query with required filters
      let dbQuery = supabase
        .from("projects")
        .select("id, job_number, name, contact_id, client_id, rev, project_type_id")
        // Include projects with rev=0 OR rev=null (new projects not yet set up)
        .or("rev.eq.0,rev.is.null")
        // Exclude projects that are marked as revisions
        .neq("is_revision", true)
        .not("job_number", "is", null);

      // Apply client filter BEFORE search .or() to ensure proper AND logic
      if (clientId) {
        dbQuery = dbQuery.eq("client_id", clientId);
      }

      // Apply search filter, ordering, and limit
      dbQuery = dbQuery
        .or(`name.ilike.%${query}%,job_number.ilike.%${query}%`)
        .order("id", { ascending: false })
        .limit(25);

      const { data, error } = await dbQuery;

      if (error) {
        throw new Error(`Failed to search base projects: ${error.message}`);
      }

      return data || [];
    } catch (error) {
      console.error("Error searching base projects:", error);
      throw error;
    }
  }

  /**
   * Get email template for a specific hold reason
   * @param {string} holdReason - The hold reason
   * @returns {Promise<string>} The email template text
   */
  static async getHoldReasonEmailTemplate(holdReason) {
    try {
      const { data, error } = await supabase
        .from("hold_reason_email_templates")
        .select("email_body_text")
        .eq("hold_reason", holdReason)
        .single();

      if (error) {
        console.error("Error fetching hold email template:", error);
        return "";
      }

      return data?.email_body_text || "";
    } catch (error) {
      console.error("Error in getHoldReasonEmailTemplate:", error);
      return "";
    }
  }

  /**
   * Send hold project email (sets hold_send_email flag to trigger workflow)
   * @param {number} projectId - The project ID
   * @param {string} emailPreview - The email body text to save
   * @param {Array} emailTo - Array of contact IDs for To field
   * @param {Array} emailCc - Array of contact IDs for CC field
   * @param {string} emailSubject - The email subject line
   * @returns {Promise<Object>} Updated project
   */
  static async sendHoldProjectEmail(
    projectId,
    emailPreview,
    emailTo = [],
    emailCc = [],
    emailSubject = "",
  ) {
    try {
      // Validate payload sizes to prevent "payload string too long" errors
      const maxTextSize = 64000; // ~64KB limit for text fields
      const maxSubjectSize = 255; // Standard email subject limit
      
      if (emailPreview && emailPreview.length > maxTextSize) {
        throw new Error(`Email content is too large (${emailPreview.length} characters). Maximum allowed is ${maxTextSize} characters.`);
      }
      
      if (emailSubject && emailSubject.length > maxSubjectSize) {
        throw new Error(`Email subject is too long (${emailSubject.length} characters). Maximum allowed is ${maxSubjectSize} characters.`);
      }

      // Get the current logged-in user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // First set hold_send_email to false to reset the trigger
      await supabase
        .from("projects")
        .update({
          hold_send_email: false,
        })
        .eq("id", projectId);

      // Then set it to true along with other updates to trigger the workflow
      const { data, error } = await supabase
        .from("projects")
        .update({
          hold_email_preview: emailPreview,
          hold_email_to: emailTo,
          hold_email_cc: emailCc,
          hold_email_subject: emailSubject,
          hold_email_sent_date: new Date().toISOString(),
          hold_send_email: true,
          last_modified_by_email: user?.email || null,
          last_modified_at: new Date().toISOString(),
        })
        .eq("id", projectId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to send hold email: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error("Error sending hold project email:", error);
      throw error;
    }
  }

  static async getDiscrepancies() {
    try {
      const { data, error } = await supabase
        .from('discrepancies')
        .select(`
          id,
          created_at,
          project_id,
          status,
          type,
          additional_information,
          created_by_name,
          sent_date,
          loom_link,
          markup_attachment
        `);

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error fetching discrepancies:', err);
      return [];
    }
  }

  /**
   * Get all email tracking data from on_hold_email_tracking table
   * @returns {Promise<Array>} Email tracking records
   */
  static async getEmailTracking() {
    try {
      const { data, error } = await supabase
        .from('on_hold_email_tracking')
        .select('*')
        .order('last_email_sent_date', { ascending: false });

      if (error) {
        console.error('Error fetching email tracking:', error);
        // Return empty array instead of throwing to prevent dashboard from breaking
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Error in getEmailTracking:', err);
      return [];
    }
  }

  /**
   * Save a modeling status exit comment
   * @param {number} projectId - The project ID
   * @param {string} previousStatus - The status being exited from
   * @param {string} commentText - The comment text (with \n for line breaks)
   * @param {string} userEmail - The current user's email
   * @returns {Promise<Object>} The created comment record
   */
  static async saveModelingStatusComment(projectId, previousStatus, commentText, userEmail) {
    try {
      // Look up employee ID by email
      const { data: employees, error: employeeError } = await supabase
        .from('employees')
        .select('id')
        .eq('email', userEmail)
        .limit(1)
        .single();

      if (employeeError) {
        console.error('Error looking up employee by email:', employeeError);
        // Continue without employee ID if lookup fails
      }

      const createdById = employees?.id || null;

      // Insert the comment
      const { data, error } = await supabase
        .from('modeling_status_comments')
        .insert({
          project_id: projectId,
          previous_modeling_status: previousStatus,
          comment_text: commentText,
          created_by: createdById,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error saving modeling status comment:', error);
        throw error;
      }

      return data;
    } catch (err) {
      console.error('Error in saveModelingStatusComment:', err);
      throw err;
    }
  }

  /**
   * Get recent hold reminders (last 20)
   * @returns {Promise<Array>} Recent hold reminders with project info
   */
  static async getRecentHoldReminders() {
    try {
      const { data, error } = await supabase
        .from('on_hold_email_tracking')
        .select(`
          *,
          projects:project_id (
            id,
            name,
            job_number,
            rev,
            modeling_status
          )
        `)
        .not('last_email_sent_date', 'is', null)
        .order('last_email_sent_date', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error getting recent hold reminders:", error);
      throw error;
    }
  }

  /**
   * Undo a hold reminder (for handling email issues)
   * @param {number} projectId - The project ID
   * @param {number} trackingId - The tracking record ID
   * @returns {Promise<Object>} Result
   */
  static async undoHoldReminder(projectId, trackingId) {
    try {
      // Reset the hold_send_email flag
      const { error: projectError } = await supabase
        .from("projects")
        .update({
          hold_send_email: false,
        })
        .eq("id", projectId);

      if (projectError) throw projectError;

      // Mark tracking record as cancelled
      // email_status: 'failed' (cancelled not allowed by email_status constraint)
      // hold_status: NULL (cancelled not allowed by check_resolved_date_consistency constraint)
      // resolved_date: NULL (required when hold_status is NULL or 'active')
      const { error: trackingError } = await supabase
        .from('on_hold_email_tracking')
        .update({
          email_status: 'failed',
          hold_status: null,
          resolved_date: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', trackingId);

      if (trackingError) throw trackingError;

      return { success: true };
    } catch (error) {
      console.error("Error undoing hold reminder:", error);
      throw error;
    }
  }

  // =====================================================================
  // RUSH PROJECT EMAIL TRACKING METHODS
  // =====================================================================

  /**
   * Get all rush project email tracking data
   * @returns {Promise<Array>} Rush project tracking records
   */
  static async getRushProjectTracking() {
    try {
      const { data, error } = await supabase
        .from('rush_project_email_tracking')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching rush project tracking:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('Error in getRushProjectTracking:', err);
      return [];
    }
  }

  /**
   * Get recent rush project notifications (last 20)
   * @returns {Promise<Array>} Recent rush notifications with project info
   */
  static async getRecentRushNotifications() {
    try {
      const { data, error } = await supabase
        .from('rush_project_email_tracking')
        .select(`
          *,
          projects:project_id (
            id,
            name,
            job_number,
            rev,
            modeling_status,
            due_date,
            created_at
          )
        `)
        .not('last_email_sent_date', 'is', null)
        .order('last_email_sent_date', { ascending: false })
        .limit(20);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error getting recent rush notifications:", error);
      throw error;
    }
  }

  /**
   * Snooze a rush project notification
   * @param {number} projectId - The project ID
   * @param {string} snoozedUntil - ISO date string when to show again
   * @param {string} snoozeReason - Why it was snoozed
   * @returns {Promise<Object>} Updated tracking record
   */
  static async snoozeRushProject(projectId, snoozedUntil, snoozeReason) {
    try {
      const { data, error } = await supabase
        .from('rush_project_email_tracking')
        .update({
          email_status: 'snoozed',
          snoozed_until: snoozedUntil,
          snooze_reason: snoozeReason,
          updated_at: new Date().toISOString(),
        })
        .eq('project_id', projectId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error snoozing rush project:", error);
      throw error;
    }
  }

  /**
   * Send rush project email and update tracking
   * Uses rush_email_* fields - requires database trigger + n8n workflow setup
   * @param {number} projectId - The project ID
   * @param {Object} emailData - Email data (preview, HTML, to, cc, subject, etc.)
   * @param {string} sentByEmail - Email of user who sent the notification
   * @returns {Promise<Object>} Updated tracking record
   */
  static async sendRushProjectEmail(projectId, emailData, sentByEmail) {
    try {
      // Validate payload sizes
      const maxTextSize = 64000;
      const maxSubjectSize = 255;

      if (emailData.preview && emailData.preview.length > maxTextSize) {
        throw new Error(`Email content is too large (${emailData.preview.length} characters). Maximum allowed is ${maxTextSize} characters.`);
      }

      if (emailData.subject && emailData.subject.length > maxSubjectSize) {
        throw new Error(`Email subject is too long (${emailData.subject.length} characters). Maximum allowed is ${maxSubjectSize} characters.`);
      }

      // Convert email addresses back to contact IDs (to match hold email payload format)
      // emailData.to and emailData.cc are arrays of "Name <email>" strings
      // We need to extract emails and find matching contact IDs
      let emailToIds = [];
      let emailCcIds = [];

      if (emailData.to && emailData.to.length > 0) {
        // Extract email addresses from "Name <email>" format
        const emailAddresses = emailData.to.map(str => {
          const match = str.match(/<(.+)>/);
          return match ? match[1].toLowerCase() : str.toLowerCase();
        });

        // Find matching contact IDs
        const { data: toContacts } = await supabase
          .from('contacts')
          .select('id, email')
          .in('email', emailAddresses);

        if (toContacts) {
          emailToIds = toContacts.map(c => c.id);
        }
      }

      if (emailData.cc && emailData.cc.length > 0) {
        const emailAddresses = emailData.cc.map(str => {
          const match = str.match(/<(.+)>/);
          return match ? match[1].toLowerCase() : str.toLowerCase();
        });

        const { data: ccContacts } = await supabase
          .from('contacts')
          .select('id, email')
          .in('email', emailAddresses);

        if (ccContacts) {
          emailCcIds = ccContacts.map(c => c.id);
        }
      }

      // Step 1: Update tracking record
      const { error: trackingError } = await supabase
        .from('rush_project_email_tracking')
        .update({
          email_status: 'sent',
          last_email_sent_date: new Date().toISOString(),
          email_sent_by: sentByEmail,
          snoozed_until: null,
          snooze_reason: null,
          updated_at: new Date().toISOString(),
        })
        .eq('project_id', projectId);

      if (trackingError) throw trackingError;

      // Step 2: Reset rush_send_email flag
      await supabase
        .from('projects')
        .update({
          rush_send_email: false,
        })
        .eq('id', projectId);

      // Small delay to ensure reset processes
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Step 3: Set rush email fields and trigger notification
      // Uses rush_email_* fields (same format as hold emails, but separate fields)
      // You'll need to create a database trigger and n8n workflow for this
      const { data, error } = await supabase
        .from('projects')
        .update({
          rush_email_preview: emailData.html || emailData.preview || '', // Send HTML version
          rush_email_to: emailToIds, // Contact IDs (same format as hold email)
          rush_email_cc: emailCcIds, // Contact IDs (same format as hold email)
          rush_email_subject: emailData.subject || '',
          rush_send_email: true, // Triggers database trigger → n8n workflow
          last_modified_by_email: sentByEmail,
          last_modified_at: new Date().toISOString(),
        })
        .eq('id', projectId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to send rush project email: ${error.message}`);
      }

      console.log("✅ Rush project email fields updated (triggers n8n workflow)", {
        projectId,
        toIds: emailToIds,
        ccIds: emailCcIds,
      });

      return data;
    } catch (error) {
      console.error("Error sending rush project email:", error);
      throw error;
    }
  }

  /**
   * Undo a rush notification (for handling email issues)
   * @param {number} projectId - The project ID
   * @param {number} trackingId - The tracking record ID
   * @returns {Promise<Object>} Result
   */
  static async undoRushNotification(projectId, trackingId) {
    try {
      // Mark tracking record as cancelled
      const { error: trackingError } = await supabase
        .from('rush_project_email_tracking')
        .update({
          email_status: 'cancelled',
          last_email_sent_date: null,
          email_sent_by: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', trackingId);

      if (trackingError) throw trackingError;

      return { success: true };
    } catch (error) {
      console.error("Error undoing rush notification:", error);
      throw error;
    }
  }

  // =====================================================================
  // RUSH MESSAGE TEMPLATES & CLIENT OVERRIDES
  // =====================================================================

  static async getRushMessageTemplates() {
    try {
      const { data, error } = await supabase
        .from('rush_message_templates')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;

      const grouped = { direct: [], relaxed: [] };
      (data || []).forEach(t => {
        if (grouped[t.tone]) grouped[t.tone].push(t);
      });
      return grouped;
    } catch (error) {
      console.error("Error loading rush message templates:", error);
      return { direct: [], relaxed: [] };
    }
  }

  static async getRushClientOverride(clientId) {
    try {
      const { data, error } = await supabase
        .from('rush_message_client_overrides')
        .select('*')
        .eq('client_id', clientId)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error loading rush client override:", error);
      return null;
    }
  }

  static async upsertRushClientOverride(clientId, { default_tone, custom_message, notes }) {
    try {
      const { data, error } = await supabase
        .from('rush_message_client_overrides')
        .upsert({
          client_id: clientId,
          default_tone: default_tone || null,
          custom_message: custom_message || null,
          notes: notes || null,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'client_id' })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error saving rush client override:", error);
      throw error;
    }
  }

  static async getClientContext(clientId) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('created_at')
        .eq('client_id', clientId);

      if (error) throw error;

      const projects = data || [];
      const projectCount = projects.length;

      if (projectCount === 0) {
        return { projectCount: 0, daysAsClient: 0, firstProjectDate: null };
      }

      const dates = projects.map(p => new Date(p.created_at)).sort((a, b) => a - b);
      const firstProjectDate = dates[0];
      const daysAsClient = Math.floor((new Date() - firstProjectDate) / (1000 * 60 * 60 * 24));

      return { projectCount, daysAsClient, firstProjectDate: firstProjectDate.toISOString() };
    } catch (error) {
      console.error("Error loading client context:", error);
      return { projectCount: 0, daysAsClient: 0, firstProjectDate: null };
    }
  }

  // =====================================================================
  // END RUSH PROJECT EMAIL TRACKING METHODS
  // =====================================================================

  /**
   * Send hold reminder email and update tracking
   * @param {number} projectId - The project ID
   * @param {Object} options - Reminder options
   * @param {boolean} options.sendEmail - Whether to send email
   * @param {number} options.daysInterval - Days until next reminder
   * @param {Object} options.emailData - Email data (preview, HTML, to, cc, subject, etc.)
   * @returns {Promise<Object>} Updated project
   */
  static async sendHoldReminder(projectId, { sendEmail, daysInterval, emailData }) {
    try {
      // Update hold_discrepancy_ids first (even if not sending email)
      if (emailData && emailData.discrepancyIds) {
        await supabase
          .from("projects")
          .update({
            hold_discrepancy_ids: emailData.discrepancyIds,
          })
          .eq("id", projectId);
      }

      // If sending email, use the existing sendHoldEmail method
      if (sendEmail && emailData) {
        await this.sendHoldEmail(
          projectId,
          emailData.emailPreview,     // Plain text preview
          emailData.emailTo,
          emailData.emailCc,
          emailData.emailSubject,
          [],                          // attachmentUrls
          emailData.discrepancies,
          emailData.emailHtml          // Pre-generated HTML from OnHoldReminderModal
        );

        // Update email tracking with recurring follow-up days
        await supabase
          .from('on_hold_email_tracking')
          .update({
            recurring_follow_up_days: daysInterval,
            last_email_sent_date: new Date().toISOString(),
          })
          .eq('project_id', projectId);
      } else {
        // Snooze: Set last_email_sent_date to NOW so the project reappears after daysInterval
        // The dashboard filter already checks: last_email_sent_date + recurring_follow_up_days <= today
        console.log("🔕 Snoozing reminder - setting last_email_sent_date to NOW for project:", projectId);
        const { error: trackingUpdateError } = await supabase
          .from('on_hold_email_tracking')
          .update({
            recurring_follow_up_days: daysInterval,
            last_email_sent_date: new Date().toISOString(),
          })
          .eq('project_id', projectId);

        if (trackingUpdateError) {
          console.error("❌ Error updating tracking:", trackingUpdateError);
          throw trackingUpdateError;
        }
        console.log(`✅ Successfully snoozed - will reappear in ${daysInterval} days`);
      }

      // Return updated project
      const { data, error } = await supabase
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error sending hold reminder:", error);
      throw error;
    }
  }
}
